import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../context";
import {
  UpdateUserInfo,
  UpdateUserPassword,
} from "../../../context/actions/auth";
import ProfilePictureUploader from "../../Dashboard/ProfileUploader";
import CircularProgress from "../../Generic/CircularProgress";

export default function VonSettings() {
  const {
    authState: { data },
    authDispatch,
    userLocationDispatch,
  } = useContext(GlobalContext);
  const user = data?.user;

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    telephone: "",
    location: "NG",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [password, setPassword] = useState({
    new_password: "",
    current_password: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    setFormData({
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      telephone: user?.telephone || "",
      location: user?.location || "NG",
    });
  }, [user]);

  // Update Profile
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!formData.firstname || !formData.lastname) {
      toast.error("First Name and Last Name are required");
      return;
    }
    setProfileLoading(true);
    const res = await UpdateUserInfo(formData, authDispatch);
    if (res) {
      toast.success("Profile updated successfully");
      userLocationDispatch({ type: 'FETCHED_DATA', payload: formData.location });
    }
    setProfileLoading(false);
  };

  const handelPasswordChange = async (e) => {
    e.preventDefault();
    if (!password.current_password || !password.new_password || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (password.new_password === confirmPassword) {
      setPasswordLoading(true);
      const res = await UpdateUserPassword(password, authDispatch);
      if (res) {
        toast.success("Password updated successfully");
        setPassword({
          new_password: "",
          current_password: "",
        });
        setConfirmPassword("");
      }
      setPasswordLoading(false);
    } else {
      toast.error("Passwords do not match");
    }
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
      {/* Personal Info Card */}
      <div className="flex-1 bg-white rounded-2xl border border-gray-150 p-6 md:p-8 shadow-sm flex flex-col gap-6 w-full">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Personal Details</h2>
          <p className="text-xs text-gray-400 mt-1">
            Update your profile picture, name, contact details, and location preferences
          </p>
        </div>

        {/* Profile Picture Uploader */}
        <div className="py-4 border-b border-gray-50 flex justify-center">
          <ProfilePictureUploader />
        </div>

        {/* Certificate Name Disclaimer — students only */}
        {!user?.is_instructor && !user?.is_admin && (
          <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-4 flex gap-3 text-amber-800 text-xs sm:text-sm">
            <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <span className="font-bold text-amber-900 block sm:inline">Important Certificate Notice:</span> The First Name and Last Name entered below will appear <span className="font-bold underline text-amber-900">exactly as shown</span> on your generated certificates. Please ensure they match your official credentials.
            </div>
          </div>
        )}

        <form onSubmit={handleUpdateUser} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">First Name</label>
              <input
                type="text"
                placeholder="First Name"
                value={formData.firstname}
                onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Last Name</label>
              <input
                type="text"
                placeholder="Last Name"
                value={formData.lastname}
                onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Phone Number</label>
              <input
                type="text"
                placeholder="Phone Number"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Preferred Timezone / Schedule</label>
              <select
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white cursor-pointer"
              >
                <option value="NG">Nigeria (WAT / West Africa Time)</option>
                <option value="US">International (EST / Eastern Standard Time)</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={profileLoading}
            className="mt-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm shadow-md shadow-blue-100 hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 self-end"
          >
            {profileLoading ? <CircularProgress /> : "Save Changes"}
          </button>
        </form>
      </div>

      {/* Security / Password Card */}
      <div className="w-full lg:w-[380px] bg-white rounded-2xl border border-gray-150 p-6 md:p-8 shadow-sm flex flex-col gap-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Change Password</h2>
          <p className="text-xs text-gray-400 mt-1">
            Keep your account secure by updating your password
          </p>
        </div>

        <form onSubmit={handelPasswordChange} className="flex flex-col gap-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Current Password</label>
            <input
              type="password"
              placeholder="Current Password"
              value={password.current_password}
              onChange={(e) => setPassword({ ...password, current_password: e.target.value })}
              className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">New Password</label>
            <input
              type="password"
              placeholder="New Password"
              value={password.new_password}
              onChange={(e) => setPassword({ ...password, new_password: e.target.value })}
              className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Confirm Password</label>
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
            />
          </div>

          <button
            type="submit"
            disabled={passwordLoading}
            className="mt-2 py-3 px-6 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
          >
            {passwordLoading ? <CircularProgress /> : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
